import { useState, useRef, useEffect, useCallback } from "react";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const LocationCombobox = ({
  value,
  items,
  placeholder,
  onChange,
  disabled = false,
  searchPlaceholder = "Cari...",
  emptyText = "Tidak ditemukan.",
  loading = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [triggerWidth, setTriggerWidth] = useState(undefined);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedItem = items.find(
    (item) => String(item.external_id) === String(value)
  );

  // Measure trigger width and update popover width
  useEffect(() => {
    if (open && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  // Reset search when popover closes
  useEffect(() => {
    if (!open) {
      setSearch("");
    }
  }, [open]);

  const handleSelect = useCallback(
    (currentValue) => {
      const item = items.find(
        (row) => String(row.external_id) === String(currentValue)
      );
      onChange(currentValue, item);
      setOpen(false);
    },
    [items, onChange]
  );

  // Client-side filtering for fast UX
  const filteredItems = search.trim()
    ? items.filter((item) =>
        item.name.toLowerCase().includes(search.toLowerCase().trim())
      )
    : items;

  const displayValue = selectedItem
    ? `${selectedItem.name}${selectedItem.zip_code ? ` (${selectedItem.zip_code})` : ""}`
    : "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={placeholder}
          disabled={disabled || (items.length === 0 && !loading)}
          className={cn(
            "h-14 w-full justify-between rounded-xl border-stone-200 text-base font-normal",
            "hover:bg-stone-50 focus:ring-emerald-500 focus-visible:ring-emerald-500",
            !value && "text-stone-400",
            value && "text-stone-900"
          )}
        >
          {loading ? (
            <span className="flex items-center gap-2 text-stone-400">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-stone-300 border-t-emerald-500" />
              Memuat...
            </span>
          ) : displayValue ? (
            <span className="truncate">{displayValue}</span>
          ) : (
            <span className="text-stone-400">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        style={{ width: triggerWidth }}
        className="p-0"
        align="start"
        sideOffset={4}
      >
        <Command shouldFilter={false} className="rounded-xl">
          <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
            <Search className="mr-2 h-4 w-4 shrink-0 text-stone-400" />
            <input
              ref={inputRef}
              type="text"
              autoFocus
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-stone-400 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="ml-1 rounded-sm opacity-70 hover:opacity-100 focus:outline-none"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <CommandList className="max-h-[280px] overflow-y-auto">
            {filteredItems.length === 0 && !loading ? (
              <CommandEmpty className="py-6 text-center text-sm text-stone-500">
                {emptyText}
              </CommandEmpty>
            ) : null}
            <CommandGroup>
              {filteredItems.map((item) => {
                const itemValue = String(item.external_id);
                const isSelected = String(value) === itemValue;
                return (
                  <CommandItem
                    key={itemValue}
                    value={itemValue}
                    onSelect={() => handleSelect(itemValue)}
                    className={cn(
                      "cursor-pointer rounded-lg px-3 py-2.5 text-sm aria-selected:bg-emerald-50",
                      isSelected && "bg-emerald-50 text-emerald-700"
                    )}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isSelected ? "opacity-100 text-emerald-600" : "opacity-0"
                      )}
                    />
                    <span className="truncate">
                      {item.name}
                      {item.zip_code ? ` (${item.zip_code})` : ""}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          {filteredItems.length > 0 && (
            <div className="border-t px-3 py-2 text-xs text-stone-400">
              {filteredItems.length} dari {items.length} hasil
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default LocationCombobox;